/**
 * The derivation tree, L0 down to L4.
 *
 * The API answers flat rows carrying a depth, which is what the database can
 * cheaply produce; the nesting is rebuilt here. Orphans - a requirement below
 * L0 with no source - are the interesting part of this screen rather than an
 * error, so they are listed rather than hidden.
 *
 * A REQUIREMENT CAN APPEAR MORE THAN ONCE. Since issue #32 a requirement may
 * name several sources and is shown beneath every one of them, because no
 * source is primary and nesting it under one would pick a winner the model
 * does not have. So a requirement id is NOT unique within this tree, and the
 * key each node is addressed by is its PATH - see nodeKey below.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { ApiError, api } from '../api'
import type { FlowdownRow } from '../schema'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesTerms, searchableText, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'

export interface FlowdownNode {
  row: FlowdownRow
  children: FlowdownNode[]
  /**
   * This node's address in the tree: the ids from the root, joined.
   *
   * NOT row.id. A requirement with two sources appears twice, so its id is
   * shared by two nodes - using it as a React key or a TreeView itemId makes
   * the two indistinguishable, and MUI then expands and selects both together.
   */
  key: string
}

/**
 * Rebuild nesting from a flat list carrying depth.
 *
 * Exported for its own test: an off-by-one here silently reparents part of the
 * tree, and a tree that renders is not the same as a tree that is right.
 */
export function toTree(rows: FlowdownRow[]): FlowdownNode[] {
  const roots: FlowdownNode[] = []
  const stack: FlowdownNode[] = []

  for (const row of rows) {
    // Drop back to this row's parent level. A row deeper than its predecessor
    // by more than one still lands somewhere sensible rather than throwing.
    while (stack.length > row.depth) stack.pop()
    const above = stack.length === 0 ? null : stack[stack.length - 1]
    const node: FlowdownNode = {
      row,
      children: [],
      key: above === null ? row.id : `${above.key}/${row.id}`,
    }
    if (above === null) roots.push(node)
    else above.children.push(node)
    stack.push(node)
  }
  return roots
}

/** Every id in a tree, for expanding it all at once. */
function allIds(nodes: FlowdownNode[]): string[] {
  return nodes.flatMap((node) => [node.key, ...allIds(node.children)])
}

/** Keep a node when it or anything below it matches. */
function prune(nodes: FlowdownNode[], text: string): FlowdownNode[] {
  const keep = (node: FlowdownNode): FlowdownNode | null => {
    const children = node.children.map(keep).filter(Boolean) as FlowdownNode[]
    // Any column, through the shared matcher, rather than the hand-listed
    // id/name/statement this used to test: a field the row gains was silently
    // unsearchable here while appearing in the tree.
    const hit = matchesTerms(searchableText(node.row), text)
    // The key is CARRIED, not rebuilt. Pruning changes which nodes survive,
    // never where one sits, and a filtered node that changed key would lose
    // its expansion state as you type.
    return hit || children.length ? { row: node.row, children, key: node.key } : null
  }
  return nodes.map(keep).filter(Boolean) as FlowdownNode[]
}

export default function Flowdown() {
  const [rows, setRows] = useState<FlowdownRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()

  useEffect(() => {
    api
      .flowdown()
      .then(setRows)
      .catch((failure: ApiError) => setError(failure))
  }, [])

  const tree = useMemo(() => (rows ? toTree(rows) : []), [rows])
  const shown = useMemo(() => (search ? prune(tree, search) : tree), [tree, search])
  const expanded = useMemo(() => allIds(shown), [shown])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Flowdown</Typography>
        <Typography variant="body2" color="text.secondary">
          Which requirements each one derives from. A requirement below L0 naming no source
          is an orphan, and is reported on the dashboard. One naming several sources appears
          under each of them — no source is primary.
        </Typography>
      </Box>

      <EntityFilterBar
        plural="requirements"
        value={search}
        onChange={setSearch}
        hint="ancestors kept"
      />

      <Paper sx={{ p: 2 }}>
        {shown.length === 0 ? (
          <EmptyState
            title={search ? `Nothing matches “${search}”` : 'The flowdown tree is empty'}
            description={
              search ? undefined : 'Use “Import from GitLab…” in the program menu to populate it.'
            }
          />
        ) : (
          <SimpleTreeView
            // Re-mounted when the filter changes so the expansion follows the
            // result rather than whatever was open before.
            key={search}
            defaultExpandedItems={expanded}
            sx={{ '& .MuiTreeItem-content': { py: 0.35 } }}
          >
            {shown.map((node) => (
              <Branch key={node.key} node={node} />
            ))}
          </SimpleTreeView>
        )}
      </Paper>
    </Stack>
  )
}

function Branch({ node }: { node: FlowdownNode }) {
  const { row } = node
  const level = row.level as number | null | undefined
  const status = String(row.status ?? '')
  const unresolved = status === 'tbd' || status === 'tbr' || status === 'tbc'

  return (
    <TreeItem
      itemId={node.key}
      label={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          {level !== null && level !== undefined && (
            <Chip label={`L${level}`} size="small" variant="outlined" sx={{ height: 20 }} />
          )}
          <RouterLink
            to={`/requirements/${encodeURIComponent(row.id)}`}
            style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
            onClick={(event) => event.stopPropagation()}
          >
            {row.id}
          </RouterLink>
          {unresolved && <Chip label={status} size="small" color="warning" sx={{ height: 20 }} />}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {String(row.statement ?? row.name ?? '')}
          </Typography>
        </Stack>
      }
    >
      {node.children.map((child) => (
        <Branch key={child.key} node={child} />
      ))}
    </TreeItem>
  )
}
